import type { WebSocketMessage } from "./types/socket";

export type IncomingCallEventType =
  | "CALL_RINGING"
  | "CALL_IN_PROGRESS"
  | "CALL_COMPLETED";

export interface IncomingCallData {
  id: string;
  from: string;
  to: string;
  time: string;
  status: string;
}

export interface NormalizedIncomingCallEvent {
  call: IncomingCallData;
  type: IncomingCallEventType;
}

const CALL_EVENT_TYPES = new Set<IncomingCallEventType>([
  "CALL_RINGING",
  "CALL_IN_PROGRESS",
  "CALL_COMPLETED",
]);

const isCallEventType = (value: unknown): value is IncomingCallEventType =>
  typeof value === "string" &&
  CALL_EVENT_TYPES.has(value as IncomingCallEventType);

const toIsoString = (value: unknown) => {
  if (typeof value !== "number" && typeof value !== "string") {
    return new Date().toISOString();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? new Date().toISOString()
    : parsed.toISOString();
};

/** Normalizes the two call envelopes currently emitted by the backend. */
export const normalizeIncomingCallEvent = (
  message: WebSocketMessage
): NormalizedIncomingCallEvent | null => {
  const isDirectEvent = isCallEventType(message.type) && Boolean(message.payload);
  const isStoreChangeEvent =
    message.event === "storeChange" &&
    message.storeId === "call" &&
    isCallEventType(message.callEventType) &&
    Boolean(message.record);

  if (!isDirectEvent && !isStoreChangeEvent) return null;

  const type = (isDirectEvent ? message.type : message.callEventType) as IncomingCallEventType;
  const source = isDirectEvent ? message.payload : message.record;
  if (!source) return null;

  const direction = source.direction;
  const from = source.from ?? "";
  const to = source.to ?? "";

  if (type === "CALL_RINGING" && (direction !== "inbound" || !from)) {
    return null;
  }

  const id =
    source.sid ??
    source.call_id ??
    source._raw?.callSid ??
    (from ? `inbound:${from}:${to}` : "");
  if (!id) return null;

  return {
    type,
    call: {
      id,
      from,
      to,
      time: toIsoString(source.timestamp ?? source.created_dt),
      status:
        source.status ??
        (type === "CALL_RINGING"
          ? "ringing"
          : type === "CALL_IN_PROGRESS"
            ? "in-progress"
            : "completed"),
    },
  };
};
